import { useState, Suspense, lazy } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'

import { VitAPI } from '@booking/vit/dist/api'
import axios from 'axios'

import vitAPI from '../api'
import Footer from '../components/Footer/Footer'
import LoadingScreen from '../components/LoadingScreen/LoadingScreen'
import bootstrapApp from '../redux/bootstrapApp'
import useOnMount from '../hooks/useOnMount'
import { GalleryContextProvider } from '../context/useGallery'
import { CartContextProvider } from '../context/useCartContext'
import routes from '../configuration/routes'
import useTrackPageViews from '../utilities/analytics/useTrackPageViews'
import { selectUserData } from '../redux/userData'

const NewBookingApp = lazy(() => import('./NewBookingApp'))
const MyAccountApp = lazy(() => import('./MyAccountApp'))
const ItineraryFinalizationApp = lazy(() =>
  import('./ItineraryFinalizationApp')
)
const AgentPortal = lazy(() => import('./AgentPortal'))

function App() {
  const [isLoading, setIsLoading] = useState(true)
  useTrackPageViews(isLoading)
  const [appError, setAppError] = useState(null)
  const [searchParams, setSearchParams] = useState(null)
  const [createCartOnMount, setCreateCartOnMount] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector(selectUserData)
  const isBlockedUser = !!user?.roles?.some((role) => role === 'trade_user')

  const handleLoginButtonClick = async (
    storageData,
    returnPathOverride = null
  ) => {
    const url = await vitAPI.generateLoginUrl()
    const { state: key } = queryString.parse(url)
    const savedData = storageData ? storageData : {}
    savedData.redirectPath = returnPathOverride
      ? returnPathOverride
      : location.pathname
    if (key && savedData) {
      // save selections to session storage to be picked up when returning
      sessionStorage.setItem(`${key}`, JSON.stringify(savedData))
    }
    window.location.href = url
  }

  const handleAgentLogin = async () => {
    const agentAuthApi = await new VitAPI(axios, {
      clientId: process.env.REACT_APP_FLOW_CLIENT_ID,
      dolliEndpoint: process.env.REACT_APP_ENDPOINT_DOLLI,
      drupalEndpoint: process.env.REACT_APP_ENDPOINT_DRUPAL,
      redirectUri: process.env.REACT_APP_AGENT_OAUTH_REDIRECT_URI,
      authorizationEndpoint: process.env.REACT_APP_AGENT_OAUTH_AUTH_ENDPOINT,
      tokenEndpoint: process.env.REACT_APP_AGENT_OAUTH_TOKEN_ENDPOINT,
      oauthClientId: process.env.REACT_APP_OAUTH_CLIENT_ID,
      crypto: window.crypto,
    })

    const url = await agentAuthApi.generateLoginUrl()
    const { state: key } = queryString.parse(url)
    const savedData = {
      redirectPath: '/agent/reservation',
    }
    if (key) {
      sessionStorage.setItem(`${key}`, JSON.stringify(savedData))
    }
    window.location.href = url
  }

  const onMount = () => {
    // bootstrap the app and check for user.
    dispatch(bootstrapApp())
      .then((res) => {
        let savedData
        if (res.searchParams?.storageKey) {
          try {
            savedData = JSON.parse(
              sessionStorage.getItem(`${res.searchParams?.storageKey}`)
            )
          } catch (e) {}
        }
        setSearchParams(
          savedData?.searchParams
            ? {
                ...savedData?.searchParams,
                storageKey: res.searchParams?.storageKey,
              }
            : res.searchParams
        )
        if (savedData?.redirectPath) {
          navigate(savedData?.redirectPath)
        }
        if (savedData?.createCartOnLoad) {
          setCreateCartOnMount(true)
        }
        if (!res.user) {
          if (location.pathname?.includes('/account')) {
            handleLoginButtonClick({
              redirectPath: window.location.pathname,
            })
          }
        }
      })
      .catch((err) =>
        setAppError(
          err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'error: please try again later.'
        )
      )
      .finally(() => {
        setIsLoading(false)
      })
  }

  useOnMount(onMount)

  const handleLogoutButtonClick = () => {
    window.location.href = `${process.env.REACT_APP_ENDPOINT_DRUPAL}/user/logout`
  }

  if (appError) {
    return <p className="my-8 text-center">{appError}</p>
  }

  if (isBlockedUser) {
    return (
      <div className="app flex flex-col items-center justify-center min-h-full max-w-[1920px] mx-auto my-0 px-16">
        <div className="bg-white p-10 border w-full max-w-[600px]">
          <p>
            Your login is associated to a Travel Agency, please use{' '}
            <a
              className="underline"
              href={process.env.REACT_APP_TRADE_AGENT_REDIRECT}
            >
              this link
            </a>{' '}
            to log into Tjuta, to access trade rates and our trade online
            reservation system.
          </p>
        </div>
      </div>
    )
  }

  return (
    <GalleryContextProvider>
      <div className="app flex flex-col min-h-full max-w-[1920px] mx-auto my-0">
        <div className="main-views flex-1 shrink-0 flex flex-col">
          {isLoading ? (
            <LoadingScreen />
          ) : (
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route
                  path={`${routes.newBookingBase}/*`}
                  element={
                    <CartContextProvider>
                      <NewBookingApp
                        initialSearchParams={searchParams}
                        onLoginButtonClick={handleLoginButtonClick}
                        onLogoutButtonClick={handleLogoutButtonClick}
                        createCartOnMount={createCartOnMount}
                      />
                    </CartContextProvider>
                  }
                />
                <Route
                  path={`${routes.itineraryFinalization}/*`}
                  element={
                    <ItineraryFinalizationApp
                      handleStartANewBooking={() =>
                        navigate(routes.newBookingBase)
                      }
                      onLoginButtonClick={() =>
                        handleLoginButtonClick(null, routes.myAccountBase)
                      }
                      onLogoutButtonClick={handleLogoutButtonClick}
                    />
                  }
                />
                <Route
                  path="/account/*"
                  element={
                    <MyAccountApp
                      handleLoginButtonClick={handleLoginButtonClick}
                      handleLogoutButtonClick={handleLogoutButtonClick}
                    />
                  }
                />
                <Route
                  path="/agent/reservation/*"
                  element={
                    <AgentPortal
                      handleLoginButtonClick={handleAgentLogin}
                      handleLogout={handleLogoutButtonClick}
                      setSearchParams={(newParams) => {
                        setSearchParams({
                          ...(searchParams || {}),
                          ...newParams,
                        })
                      }}
                      handleNewBookingSearch={(newParams) => {
                        setSearchParams({
                          ...(searchParams || {}),
                          ...newParams,
                        })
                        navigate(
                          newParams?.flow === 'tours' ? '/book/tours' : '/book'
                        )
                      }}
                    />
                  }
                />
              </Routes>
            </Suspense>
          )}
        </div>
        <Footer />
      </div>
    </GalleryContextProvider>
  )
}

export default App
